import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ChevronLeft, Plus, Eye, Play } from "lucide-react";
import { BASE_URL } from "../apiConfig";
import "./allmarts.css";

const MartsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { inputID } = useParams();
  const projectName = location.state?.projectName;
  const projectId = location.state?.projectId;
  const clientID = location.state?.clientID;
  const roleId = location.state?.roleId;
  const isChildMartsView = Boolean(location.state?.isChildMartsView);
  const parentMartId = location.state?.parentMartId;
  const parentMartName = location.state?.parentMartName;

  const [isLoading, setIsLoading] = useState(false);
  const [marts, setMarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newMartName, setNewMartName] = useState("");
  const [generatingMartId, setGeneratingMartId] = useState(null);

  useEffect(() => {
    fetchMarts();
  }, [isChildMartsView, parentMartId]);

  const fetchMarts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const url = isChildMartsView
        ? `${BASE_URL}/mart_router/get_list_of_child_marts?parent_mart_id=${parentMartId}`
        : `${BASE_URL}/mart_router/get_list_of_marts?input_id=${inputID}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch marts");
      }

      const data = await response.json();
      setMarts(data);
    } catch (error) {
      console.error("Error fetching marts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isChildMartsView) {
      navigate(`/architect/${inputID}/marts`, {
        state: {
          mart_id: parentMartId,
          projectId,
          martsFileName: parentMartName,
          inputID,
          clientID,
          roleId,
        },
      });
      return;
    }
    navigate(`/architect/${inputID}/summary`, {
      state: { projectId, inputID, projectName, clientID, roleId },
    });
  };

  const handleViewMart = (mart) => {
    if (!mart.generated_marts) {
      alert("Mart has not been generated yet. Please generate it first.");
      return;
    }
    console.log("View mart:", mart.id);
    // Navigate to mart details/view page
    navigate(`/architect/${inputID}/marts`, {
        state: {
          mart_id: mart.id,
          projectId,
          martsFileName: mart.name,
          inputID,
          clientID,
          roleId,
          filterId: mart.filter_id,
        }
        });
  };

  const handleGenerateMart = async (mart) => {
    setGeneratingMartId(mart.id);
    try {
      // Navigate to norms page to configure and generate
      navigate(`/architect/${inputID}/norms`, {
        state: { mart_id: mart.id, projectId, mart_name: mart.name, inputID, clientID, roleId },
      });
    } catch (error) {
      console.error("Error navigating to generate mart:", error);
    } finally {
      setGeneratingMartId(null);
    }
  };

  const handleCreateMarts = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(`${BASE_URL}/mart_router/create_mart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          input_id: inputID,
          name: newMartName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create mart: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Close modal and reset
      setIsCreateModalOpen(false);
      setNewMartName("");
      
      // Refresh the marts list
      await fetchMarts();
      
      // Navigate to norms page for the newly created mart
      navigate(`/architect/${inputID}/norms`, {
        state: { mart_id: data.id, projectId, mart_name: newMartName, inputID, clientID, roleId },
      });
    } catch (err) {
      console.error("Error creating mart:", err.message);
      alert("Failed to create mart. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "P.M." : "A.M.";
    hours = hours % 12 || 12;

    return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  return (
    <div className="marts-container">
      <div className="marts-card">
        <div className="allmarts-header">
          <button className="marts-title" onClick={handleBack}>
            <span className="marts-back-arrow">
              <ChevronLeft />
            </span>
            <h1>{isChildMartsView ? "Child Marts" : "Data Marts"}</h1>
          </button>
          {!isChildMartsView && (
            <button
              className="marts-create-btn"
              onClick={() => setIsCreateModalOpen(true)}
            >
              {/* <Plus size={18} /> */}
              Create New Mart
            </button>
          )}
        </div>

        {loading ? (
          <div className="marts-loading">
            <div className="marts-spinner"></div>
            <p>Loading marts...</p>
          </div>
        ) : marts.length === 0 ? (
          <div className="marts-empty">
            <p>{isChildMartsView ? "No child marts available" : "No marts available"}</p>
          </div>
        ) : (
          <div className="marts-list">
            {marts.map((mart) => (
              <div key={mart.id} className="marts-item">
                <div className="marts-item-header">
                  <h3 className="marts-item-name">{mart.name}</h3>
                  <span
                    className={`marts-item-status ${
                      mart.generated_marts
                        ? "marts-status-generated"
                        : "marts-status-pending"
                    }`}
                  >
                    {mart.generated_marts ? "Generated" : "Pending"}
                  </span>
                </div>
                <div className="marts-item-details">
                  <div className="marts-item-info">
                    <span className="marts-item-label">Created:</span>
                    <span className="marts-item-value">
                      {formatDate(mart.created_at)}
                    </span>
                  </div>
                  {/* <div className="marts-item-info">
                    <span className="marts-item-label">Input ID:</span>
                    <span className="marts-item-value-mono">
                      {mart.input_id.slice(0, 8)}...
                    </span>
                  </div> */}
                </div>
                <div className="marts-item-actions">
                  <button
                    className={`marts-action-btn marts-btn-view ${
                      !mart.generated_marts ? "marts-btn-disabled" : ""
                    }`}
                    onClick={() => handleViewMart(mart)}
                    disabled={!mart.generated_marts}
                    title={
                      mart.generated_marts
                        ? "View Mart"
                        : "Generate mart first to view"
                    }
                  >
                    <Eye size={16} />
                    View
                  </button>
                  {!mart.generated_marts && (
                    <button
                      className="marts-action-btn marts-btn-generate"
                      onClick={() => handleGenerateMart(mart)}
                      disabled={generatingMartId === mart.id}
                      title="Generate Mart"
                    >
                      {generatingMartId === mart.id ? (
                        <>
                          <div className="marts-spinner-small"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Play size={16} />
                          Generate
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {isCreateModalOpen && (
          <div className="marts-modal-overlay">
            <div className="marts-modal">
              <div className="marts-modal-header">
                <span
                  className="marts-modal-back-arrow"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setNewMartName("");
                  }}
                >
                  <ChevronLeft />
                </span>
                <h1 className="marts-modal-title">Create New Mart</h1>
              </div>
              <div className="marts-modal-content">
                <div className="marts-input-group">
                  <label>Mart Name</label>
                  <input
                    type="text"
                    value={newMartName}
                    onChange={(e) => setNewMartName(e.target.value)}
                    className="marts-input"
                    placeholder="Enter mart name"
                    autoFocus
                  />
                </div>
                <div className="marts-modal-actions">
                  <button
                    className="marts-modal-btn marts-btn-cancel"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setNewMartName("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="marts-modal-btn marts-btn-create"
                    onClick={handleCreateMarts}
                    disabled={!newMartName.trim() || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="marts-spinner-small"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Create Mart
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MartsPage;